import {
  OpenAiChatMessageView,
  OpenAiSourceLocation,
  useTranslateOpenAiMessage,
} from "../api-generated";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import Blob from "./Blob";
import clsx from "clsx";
import { useCurrentDevice } from "../hooks";
import React, { useEffect, useState } from "react";
import Ellipsis from "./Ellipsis";

interface Props {
  sourceId: number;
  message: OpenAiChatMessageView;
  location: OpenAiSourceLocation;
  queryKey: QueryKey;
  //these are potentially undefined for when nb of messages changes
  //and useEffect hasn't yet updated the multiselect state
  //to reflect the new nb of messages
  wordsInMultipleSelect?: number[][];
  onToggleInMultiSelect: (tokenIndex: number, sentenceIndex: number) => void;
}

const OpenAiChatMessage: React.FC<Props> = ({
  sourceId,
  message,
  location,
  queryKey,
  onToggleInMultiSelect,
  wordsInMultipleSelect,
}) => {
  const [isShowingWithTranslations, setIsShowingWithTranslations] = useState(
    !!message.blobs[0].englishSentenceTranslation
  );

  useEffect(
    () =>
      setIsShowingWithTranslations(
        !!message.blobs[0].englishSentenceTranslation
      ),
    [!!message.blobs[0].englishSentenceTranslation]
  );

  //TODO: just checking first blob for translation
  const hasEnglishTranslations = !!message.blobs[0].englishSentenceTranslation;
  const { isPhone } = useCurrentDevice();

  const qc = useQueryClient();

  const translateMessageMut = useTranslateOpenAiMessage();

  const translateMessage = () => {
    translateMessageMut.mutate(
      { sourceId, messageIndex: location.messageIndex! },
      {
        onSuccess: () => {
          //TODO: this endpoint should get message back,
          //or just tranlsations, and set it in cache
          qc.refetchQueries(queryKey);
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={clsx(["flex gap-4", { "pl-4": !isPhone, "pl-2": isPhone }])}
      >
        <div className="font-bold">{message.role}</div>
        {message.role === "assistant" && (
          <>
            {hasEnglishTranslations && !isShowingWithTranslations && (
              <button onClick={() => setIsShowingWithTranslations(true)}>
                <div className="flex flex-col justify-center">
                  <div className="text-xs hover:underline">
                    Show translations
                  </div>
                </div>
              </button>
            )}
            {hasEnglishTranslations && isShowingWithTranslations && (
              <button onClick={() => setIsShowingWithTranslations(false)}>
                <div className="flex flex-col justify-center">
                  <div className="text-xs hover:underline">
                    Hide translations
                  </div>
                </div>
              </button>
            )}
            {!hasEnglishTranslations && (
              <button
                onClick={translateMessage}
                disabled={translateMessageMut.isLoading}
              >
                <div className="flex flex-col justify-center">
                  <div className="text-xs hover:underline">
                    {translateMessageMut.isLoading ? (
                      <>
                        Translating message
                        <Ellipsis />
                      </>
                    ) : (
                      <>Translate message</>
                    )}
                  </div>
                </div>
              </button>
            )}
          </>
        )}
      </div>
      <div className={clsx(["w-full", { "px-4": !isPhone }])}>
        <div
          className={clsx([
            "bg-gray-100 w-full p-2",
            { "rounded px-4": !isPhone },
          ])}
          //TODO: this is all fucked up
        >
          {message.blobs.map((sentenceBlob, sentenceIndex) => (
            <React.Fragment key={sentenceIndex}>
              <Blob
                blob={sentenceBlob}
                location={
                  { ...location, sentenceIndex } as OpenAiSourceLocation
                }
                tokensInMultiSelect={
                  wordsInMultipleSelect
                    ? wordsInMultipleSelect[sentenceIndex]
                    : []
                }
                onTokenClick={(_, tokenIndex) => {
                  onToggleInMultiSelect(tokenIndex, sentenceIndex);
                }}
                overrideShowTranslations={isShowingWithTranslations}
                onlyFocusOnHover
              />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OpenAiChatMessage;
